import { FC, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';
import { DEAL_FORM } from 'modules/account/contact/forms/schema';
import { useGetDeals } from 'modules/account/sales/hooks';
import { Button } from 'ui/components/common';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import useModal, { useExternalModal } from 'ui/partials/Modal/useModal';
import { SIZE } from 'utils/constants';
import { cn, convertToAmount } from 'utils/helpers';
import { Stage } from 'utils/types';
import SelectAvatarPopover from 'ui/components/form/SelectAvatar/SelectAvatarPopover';
import CreateSimpleDealModal from 'modules/account/sales/components/CreateSimpleDealModal';

const AssignDealForm: FC = () => {
  const [dealKeyword, setDealKeyword] = useState('');
  const { data: deals } = useGetDeals({ keyword: dealKeyword });
  const { values, handleSubmit, setFieldValue, setValues, isSubmitting } =
    useFormikContext<DEAL_FORM>();

  const { hideModal } = useModal();
  const { showModal: showExternalModal, hideModal: hideExternalModal } =
    useExternalModal();

  const dealItems = useMemo(() => {
    return deals?.data.map<AvatarOption>((deal) => ({
      itemLabel: deal.name,
      value: deal._id,
      description: `$${convertToAmount(deal.amount)} | ${(deal.stageId as Stage)?.name}`,
    }));
  }, [deals?.data]);

  const defaultDealId = useMemo(
    () => dealItems?.filter((i) => values.dealId?.includes(i.value)),
    [dealItems, values.dealId],
  );

  const handleToggleDeal = () => {
    showExternalModal({
      title: 'Create Deal',
      disableOutsideClick: true,
      content: (
        <CreateSimpleDealModal
          afterCreate={(deal) => {
            setValues((prevVal) => ({ dealId: [...prevVal.dealId, deal._id] }));
            hideExternalModal();
          }}
          onCancel={hideExternalModal}
        />
      ),
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-y-4">
        <SelectAvatarPopover
          label="Deals"
          selected={defaultDealId}
          size={SIZE.Small}
          values={dealItems}
          onInputChange={(val) => setDealKeyword(val)}
          onSelect={(val) =>
            setFieldValue('dealId', values.dealId?.concat(val))
          }
          onClickSelected={(selected) =>
            setFieldValue(
              'dealId',
              values.dealId?.filter((value) => value !== selected),
            )
          }
          onClear={() => setFieldValue('dealId', [])}
          onClickCreate={handleToggleDeal}
          placeholder="Select deals"
        />
      </div>
      <div
        className={cn(
          'pt-[22px] mt-[22px]',
          'flex justify-end gap-2',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={() => hideModal()}
          label="CANCEL"
          intent="tertiary"
          disabled={isSubmitting}
        />
        <Button type="submit" label="SAVE" disabled={isSubmitting} />
      </div>
    </form>
  );
};

export default AssignDealForm;
